import ReactDOM from "react-dom/client";
import App from "./App";
import "./scss/main.scss";
import store from "./store";
import { initKeycloak } from "@services/UserService";
import { loggerService } from "@services/LoggerService";
import GlobalStatesWrapper from "@wrappers/GlobalStatesWrapper";
import {
    getReportTypes,
    registerUser,
    setUserDetails,
} from "@ts/retrieve-data";
import {
    connectGrowthBook,
    getDecryptedUserDetails,
    growthbook,
    setProfiles,
} from "@ts/utils";
import { identifyUserFromStorage } from "@services/HeapService";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { initializeFiltersFromUrl } from "@ts/revstack/filters";
import { updateUserData } from "./features/userDataSlice";
import { BrowserRouter } from "react-router-dom";
import ServerError from "@pages/server-error/ServerError";
import * as Sentry from "@sentry/react";

// Initialize Sentry first before anything else
loggerService();

// Render loading screen immediately
const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
);

// Helper function to render the 500 error page
export const renderServerError = () => {
    // Remove the initial loader
    const initialLoader = document.getElementById("initial-loader");
    if (initialLoader) {
        initialLoader.remove();
    }
    // Show 500 error page
    root.render(<ServerError />);
};

initKeycloak(async () => {
    try {
        await setUserDetails();
        store.dispatch(updateUserData(getDecryptedUserDetails()));
        await registerUser();
        await setProfiles();
        await initializeFiltersFromUrl();
        await connectGrowthBook();
        await store.dispatch(getReportTypes());

        // Identify user in Heap analytics after authentication is complete
        identifyUserFromStorage();

        // Replace loading screen with the actual app
        root.render(
            <GrowthBookProvider growthbook={growthbook}>
                <GlobalStatesWrapper>
                    <BrowserRouter>
                        <App />
                    </BrowserRouter>
                </GlobalStatesWrapper>
            </GrowthBookProvider>
        );
    } catch (err) {
        console.error("Initialization Error:", err);

        // Capture error in Sentry with context
        Sentry.captureException(err, {
            level: "fatal",
            tags: {
                error_type: "initialization_error",
                location: "main.tsx",
            },
            contexts: {
                initialization: {
                    step: "post_keycloak_init",
                    timestamp: new Date().toISOString(),
                    url: window.location.href,
                },
            },
            extra: {
                userAgent: navigator.userAgent,
                errorMessage: err instanceof Error ? err.message : String(err),
                errorStack: err instanceof Error ? err.stack : undefined,
            },
        });

        // Try to get user details if available
        try {
            const userDetails = store.getState().userData?.value;
            if (userDetails?.user_id) {
                Sentry.setUser({
                    id: userDetails.user_id.toString(),
                    email: userDetails.email,
                    username: `${userDetails.first_name} ${userDetails.last_name}`,
                });
            }
        } catch (userError) {
            // Ignore user detail errors during error handling
        }

        // Show 500 error page on initialization failure
        renderServerError();
    }
});
